import { useState, useCallback } from 'react';
import { Archive, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';

interface ArchivePanelOptions {
  /** Default "from" date offset in days (default 90) */
  defaultFromDays?: number;
  onSearch: (from: string, to: string) => Promise<void>;
}

export interface ArchivePanelState {
  archiveMode: boolean;
  panelOpen: boolean;
  from: string;
  to: string;
  isLoading: boolean;
  setFrom: (v: string) => void;
  setTo: (v: string) => void;
  openPanel: () => void;
  exitArchiveMode: () => void;
  search: () => void;
}

function toDateInput(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function useArchivePanel({ defaultFromDays = 90, onSearch }: ArchivePanelOptions): ArchivePanelState {
  const [archiveMode, setArchiveMode] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const defaultFrom = toDateInput(new Date(Date.now() - defaultFromDays * 24 * 60 * 60 * 1000));
  const defaultTo = toDateInput(new Date());

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  const openPanel = useCallback(() => {
    setPanelOpen(true);
  }, []);

  const exitArchiveMode = useCallback(() => {
    setArchiveMode(false);
    setPanelOpen(false);
    setFrom(toDateInput(new Date(Date.now() - defaultFromDays * 24 * 60 * 60 * 1000)));
    setTo(toDateInput(new Date()));
  }, [defaultFromDays]);

  const search = useCallback(async () => {
    setIsLoading(true);
    try {
      await onSearch(from, to);
      setArchiveMode(true);
      setPanelOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, [from, to, onSearch]);

  return { archiveMode, panelOpen, from, to, isLoading, setFrom, setTo, openPanel, exitArchiveMode, search };
}

interface ArchiveHeaderProps {
  label: string;
  count: number;
  panel: ArchivePanelState;
}

/**
 * Renders the section header row with the "Older …" / "Back to recent" button
 * and (when panelOpen) the inline date-range picker.
 *
 * Usage:
 * ```tsx
 * <ArchiveHeader label="History" count={runs.length} panel={panel} />
 * ```
 */
export function ArchiveHeader({ label, count, panel }: ArchiveHeaderProps) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          {panel.archiveMode ? 'Archive View' : label}
          <Badge variant="outline" className="ml-1">{count}</Badge>
          {panel.archiveMode && (
            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
              {panel.from} → {panel.to}
            </span>
          )}
        </h3>
        {panel.archiveMode ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 text-xs"
            onClick={panel.exitArchiveMode}
          >
            <X className="h-3.5 w-3.5" />
            Back to recent
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 text-xs"
            onClick={panel.openPanel}
          >
            <Archive className="h-3.5 w-3.5" />
            Older {label.toLowerCase()}
          </Button>
        )}
      </div>

      {panel.panelOpen && !panel.archiveMode && (
        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/50">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">From</label>
            <input
              type="date"
              value={panel.from}
              onChange={(e) => panel.setFrom(e.target.value)}
              className="rounded border border-gray-300 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">To</label>
            <input
              type="date"
              value={panel.to}
              onChange={(e) => panel.setTo(e.target.value)}
              className="rounded border border-gray-300 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
          </div>
          <Button
            size="sm"
            className="h-7 text-xs"
            disabled={panel.isLoading || !panel.from}
            onClick={panel.search}
          >
            {panel.isLoading ? 'Searching…' : 'Search'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={panel.exitArchiveMode}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
